import React, { useCallback, useContext, useEffect, useState } from 'react';

import { RicosContext } from './../../../ricos-context';

import { ToolbarButtons } from '../../models';

type SizeResult = {
  visibleButtons: ToolbarButtons;
  overflownButtons: ToolbarButtons;
};

type SizeCalculatorProps = {
  width: number;
  toolbarButtons: ToolbarButtons;
  onlyShownButtons?: string[];
};

const MORE_BUTTON_WIDTH = 30;
const GAP_BETWEEN_BUTTONS = 4;

const Overflow = ({
  width,
  toolbarButtons,
  onChange,
  onlyShownButtons,
}: {
  width: number;
  toolbarButtons: ToolbarButtons;
  onChange: (result: SizeResult) => void;
  onlyShownButtons?: string[];
}) => {
  const { jsdomMode } = useContext(RicosContext);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const buttonsToRender = new ToolbarButtons(
    toolbarButtons.toolbarButtons
      .filter(button => onlyShownButtons === undefined || onlyShownButtons.includes(button.getId()))
      .sort(
        (a, b) =>
          (onlyShownButtons?.findIndex(id => id === a.getId()) ?? 0) -
          (onlyShownButtons?.findIndex(id => id === b.getId()) ?? 0)
      )
  );

  const alwaysOverflownButtons = toolbarButtons.toolbarButtons.filter(
    button => onlyShownButtons !== undefined && !onlyShownButtons.includes(button.getId())
  );
  useEffect(() => {
    let sumVisible = 0;
    const visibleButtons: ToolbarButtons = new ToolbarButtons([]);
    const overflownButtons: ToolbarButtons = new ToolbarButtons([]);

    if (!containerRef) {
      return;
    }

    const elements = Array.from(containerRef.children).map((child: Element) => {
      return {
        child,
        width: Math.ceil(child.getBoundingClientRect().width),
      };
    });
    let stackIsFull = false;
    elements.forEach((element, index) => {
      if ((!stackIsFull && sumVisible + element.width <= width - MORE_BUTTON_WIDTH) || jsdomMode) {
        visibleButtons.addButton(buttonsToRender.getButtonByIndex(index));

        sumVisible += element.width + GAP_BETWEEN_BUTTONS;
      } else {
        stackIsFull = true;
        overflownButtons.addButton(buttonsToRender.getButtonByIndex(index));
      }
    });

    alwaysOverflownButtons.forEach(button => {
      overflownButtons.addButton(button);
    });

    onChange({
      visibleButtons,
      overflownButtons,
    });
  }, [width, toolbarButtons, containerRef]);

  return (
    <div
      style={{ visibility: 'hidden', position: 'absolute', display: 'flex' }}
      ref={setContainerRef}
    >
      {buttonsToRender.getButtonsElements()}
    </div>
  );
};

export const useSizeCalculator = ({
  width,
  toolbarButtons,
  onlyShownButtons,
}: SizeCalculatorProps) => {
  const [data, setData] = useState<SizeResult>();

  const OverflowCalculator = useCallback(
    () => (
      <Overflow
        width={Number(width)}
        toolbarButtons={toolbarButtons}
        onChange={setData}
        onlyShownButtons={onlyShownButtons}
      />
    ),
    [width, toolbarButtons, onlyShownButtons, setData]
  );

  return { data, OverflowCalculator };
};
